import { useState, useEffect } from "react";
import AOS from "aos";
import "aos/dist/aos.css";
import { foodCard } from "../constants/foodcard";
import { Link } from "react-router-dom";

const FoodCard = ({ selectedCategory }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [animationKey, setAnimationKey] = useState(0);
  const itemsPerPage = 8;

  useEffect(() => {
    AOS.init({
      duration: 800,
      once: false,
    });
  }, []);

  // Reset when category changes
  useEffect(() => {
    setCurrentPage(1);
    setAnimationKey((prev) => prev + 1);
  }, [selectedCategory]);

  // Smooth scroll to top on pagination change
  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }, [currentPage]);

  const filteredFoods =
    selectedCategory === "All"
      ? foodCard
      : foodCard.filter((item) => item.category === selectedCategory);

  const totalPages = Math.ceil(filteredFoods.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentFoods = filteredFoods.slice(
    startIndex,
    startIndex + itemsPerPage,
  );

  const goToPage = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
      setAnimationKey((prev) => prev + 1);
    }
  };

  return (
    <div className="w-full max-w-screen-xl mx-auto px-2">
      {/* Food Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4">
        {currentFoods.map((item, index) => (
          <div
            key={index + "-" + animationKey}
            data-aos="fade-up"
            className="flex flex-col bg-white rounded-2xl shadow-soft border border-coffee-light/20 overflow-hidden transform transition-all duration-300 hover:shadow-glow hover:-translate-y-2 h-full"
          >
            {/* 1. Image Container: Fixed Height & Aspect Ratio */}
            <div className="relative w-full aspect-square bg-gray-50 flex items-center justify-center overflow-hidden p-4">
              <img
                src={item.image}
                alt={item.title}
                className="w-full h-full object-contain transition-transform duration-500 hover:scale-110"
              />
            </div>

            {/* 2. Content Section: flex-grow ensures the yellow box stays at the bottom */}
            <div className="flex flex-col flex-grow bg-cream-dark/50 py-6 px-5 border-t border-coffee-light/10">
              <div className="flex justify-between items-center gap-3">
                <div className="min-w-0 flex-1">
                  {" "}
                  {/* min-w-0 is critical for truncate to work */}
                  <h3 className="text-md md:text-lg font-bold text-coffee-dark truncate uppercase tracking-wide">
                    {item.title}
                  </h3>
                  <p className="text-xs text-gray-500 mt-1 line-clamp-2 leading-tight">
                    {item.desc}
                  </p>
                </div>

                {/* 3. Button: Fixed size */}
                <Link
                  to="https://t.me/Manticcup99"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="shrink-0 w-10 h-10 md:w-12 md:h-12 rounded-full bg-accent-gold text-coffee-dark flex items-center justify-center hover:bg-yellow-400 transition-colors shadow-glow"
                >
                  <svg
                    className="w-5 h-5 md:w-6 md:h-6"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M12 4v16m8-8H4"
                    />
                  </svg>
                </Link>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center items-center mt-10 mb-10 space-x-2 flex-wrap">
          {/* Prev Button */}
          <button
            onClick={() => goToPage(currentPage - 1)}
            disabled={currentPage === 1}
            className={`px-4 py-2 rounded-lg border font-medium text-sm transition-colors ${
              currentPage === 1
                ? "bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed"
                : "bg-accent-gold text-coffee-dark border-transparent hover:bg-yellow-400 shadow-sm"
            }`}
          >
            Prev
          </button>

          {/* Dynamic Pagination Logic */}
          {(() => {
            const pageButtons = [];
            const maxVisible = 5;
            if (totalPages <= maxVisible) {
              for (let i = 1; i <= totalPages; i++) {
                pageButtons.push(
                  <button
                    key={i}
                    onClick={() => goToPage(i)}
                    className={`px-4 py-2 rounded-lg border font-medium text-sm transition-colors ${
                      currentPage === i
                        ? "bg-accent-gold text-coffee-dark border-transparent shadow-sm"
                        : "bg-white text-coffee-medium border-coffee-light/30 hover:bg-accent-gold/20"
                    }`}
                  >
                    {i}
                  </button>,
                );
              }
            } else {
              const showLeftEllipsis = currentPage > 3;
              const showRightEllipsis = currentPage < totalPages - 2;
              const startPage = showLeftEllipsis
                ? Math.max(2, currentPage - 1)
                : 2;
              const endPage = showRightEllipsis
                ? Math.min(totalPages - 1, currentPage + 1)
                : totalPages - 1;

              // First page
              pageButtons.push(
                <button
                  key={1}
                  onClick={() => goToPage(1)}
                  className={`px-4 py-2 rounded-lg border font-medium text-sm transition-colors ${currentPage === 1 ? "bg-accent-gold text-coffee-dark border-transparent shadow-sm" : "bg-white text-coffee-medium border-coffee-light/30 hover:bg-accent-gold/20"}`}
                >
                  1
                </button>,
              );

              if (showLeftEllipsis)
                pageButtons.push(
                  <span key="left" className="px-2">
                    ...
                  </span>,
                );

              for (let i = startPage; i <= endPage; i++) {
                pageButtons.push(
                  <button
                    key={i}
                    onClick={() => goToPage(i)}
                    className={`px-4 py-2 rounded-lg border font-medium text-sm transition-colors ${currentPage === i ? "bg-accent-gold text-coffee-dark border-transparent shadow-sm" : "bg-white text-coffee-medium border-coffee-light/30 hover:bg-accent-gold/20"}`}
                  >
                    {i}
                  </button>,
                );
              }

              if (showRightEllipsis)
                pageButtons.push(
                  <span key="right" className="px-2">
                    ...
                  </span>,
                );
              // Last page
              pageButtons.push(
                <button
                  key={totalPages}
                  onClick={() => goToPage(totalPages)}
                  className={`px-4 py-2 rounded-lg border font-medium text-sm transition-colors ${currentPage === totalPages ? "bg-accent-gold text-coffee-dark border-transparent shadow-sm" : "bg-white text-coffee-medium border-coffee-light/30 hover:bg-accent-gold/20"}`}
                >
                  {totalPages}
                </button>,
              );
            }
            return pageButtons;
          })()}

          {/* Next Button */}
          <button
            onClick={() => goToPage(currentPage + 1)}
            disabled={currentPage === totalPages}
            className={`px-4 py-2 rounded-lg border font-medium text-sm transition-colors ${
              currentPage === totalPages
                ? "bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed"
                : "bg-accent-gold text-coffee-dark border-transparent hover:bg-yellow-400 shadow-sm"
            }`}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default FoodCard;
